/**
 * Page de vérification du code 2FA pour les administrateurs.
 */
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

const TwoFactorVerify = () => {
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const navigate = useNavigate();
  const location = useLocation();
  const { verify2FA, resend2FA } = useAuth();

  const email = location.state?.email;

  useEffect(() => {
    // Si pas d'email dans le state, rediriger vers login
    if (!email) {
      navigate('/login');
    }
  }, [email, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccessMessage('');
    setLoading(true);

    // SÉCURITÉ: Utiliser le AuthContext qui gère les cookies httpOnly
    const result = await verify2FA(email, code);

    if (result.success) {
      // Rediriger vers le dashboard
      navigate('/dashboard');
    } else {
      setError(result.error);
    }

    setLoading(false);
  };

  const handleResend = async () => {
    setError('');
    setSuccessMessage('');
    setResending(true);

    // SÉCURITÉ: Utiliser le AuthContext qui gère les cookies httpOnly
    const result = await resend2FA(email);

    if (result.success) {
      setSuccessMessage('Un nouveau code a été envoyé à votre adresse email.');
      setCode('');
    } else {
      setError(result.error);
    }

    setResending(false);
  };

  const handleCodeChange = (e) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 6);
    setCode(value);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-8 sm:py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-6 sm:space-y-8">
        <div>
          <h2 className="mt-4 sm:mt-6 text-center text-2xl sm:text-3xl font-extrabold text-gray-900">
            Vérification en deux étapes
          </h2>
          <p className="mt-2 text-center text-xs sm:text-sm text-gray-600">
            Un code de vérification a été envoyé à votre adresse email
          </p>
          <p className="mt-1 text-center text-xs sm:text-sm font-medium text-gray-900">
            {email}
          </p>
        </div>

        <form className="mt-6 sm:mt-8 space-y-4 sm:space-y-6" onSubmit={handleSubmit}>
          {error && (
            <div className="bg-red-100 border border-red-400 text-red-700 px-3 sm:px-4 py-2 sm:py-3 rounded text-xs sm:text-sm">
              {error}
            </div>
          )}

          {successMessage && (
            <div className="bg-green-100 border border-green-400 text-green-700 px-3 sm:px-4 py-2 sm:py-3 rounded text-xs sm:text-sm">
              {successMessage}
            </div>
          )}

          <div>
            <label htmlFor="code" className="block text-sm font-medium text-gray-700 mb-2">
              Code de vérification
            </label>
            <input
              id="code"
              name="code"
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              required
              maxLength="6"
              className="appearance-none block w-full px-3 py-3 border border-gray-300 rounded-md text-center text-2xl font-bold tracking-widest placeholder-gray-400 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
              placeholder="000000"
              value={code}
              onChange={handleCodeChange}
              autoComplete="off"
            />
            <p className="mt-2 text-xs text-gray-500 text-center">
              Le code expire dans 5 minutes
            </p>
          </div>

          <div>
            <button
              type="submit"
              disabled={loading || code.length !== 6}
              className="group relative w-full flex justify-center py-2.5 sm:py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 active:bg-primary-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {loading ? 'Vérification...' : 'Vérifier'}
            </button>
          </div>

          <div className="text-center">
            <button
              type="button"
              onClick={handleResend}
              disabled={resending}
              className="text-xs sm:text-sm font-medium text-primary-600 hover:text-primary-500 active:text-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {resending ? 'Envoi en cours...' : 'Renvoyer le code'}
            </button>
          </div>

          <div className="text-center">
            <button
              type="button"
              onClick={() => navigate('/login')}
              className="text-xs sm:text-sm font-medium text-gray-600 hover:text-gray-500 active:text-gray-700 transition-colors"
            >
              Retour à la connexion
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default TwoFactorVerify;
